import { useState, useEffect, useCallback, useRef } from 'react'
import { LinkifyIt } from 'linkify-it'
import tlds from 'tlds'
import UserHoverCard from '../../components/ui/UserHoverCard'
import ReportModal from '../../components/ui/ReportModal'
import Avatar from '../../components/ui/Avatar'
import Tooltip from '../../components/ui/Tooltip'
import { api, ApiError } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { openRazorpayCheckout } from '../../lib/razorpay'
import { REPLIES_PAGE_SIZE, REPORTING_ENABLED, COLOR_SUCCESS, COLOR_ERROR, COLOR_AI_BG, COLOR_AI_BORDER, COMMENT_CONTENT_TRUNCATE, COPY_FEEDBACK_MS, DOM_EVENTS } from '../../lib/config'
import { timeAgoShort, contentPreview, decodeEntities } from '../../lib/avatar'
import { agentsApi } from '../../store/api/agentsApi'
import { store } from '../../store'
import LikeButton from './LikeButton'
import ReplyComposer from './ReplyComposer'
import { AIMarkdownContent } from './AIMarkdownContent'
import type { Comment } from './commentTypes'
import { useToast } from '../../context/ToastContext'
import s from '../../styles/pages/DiscussionDetailPage.module.css'

const MAX_CONTENT_LEN = COMMENT_CONTENT_TRUNCATE

// Built once, reused for every comment render.
// - fuzzyLink: true enables matching bare domains with no http(s):// prefix
//   (linkify-it's default is scheme-only URLs).
// - .tlds(tlds) swaps in the `tlds` package's list — the real, regularly
//   updated IANA registry — instead of linkify-it's bundled ~2015 snapshot,
//   which is missing most modern gTLDs (.app, .dev, .xyz, .store, etc.).
// - fuzzyEmail is off — we don't want to turn a stray email address into a
//   mailto: link inside a comment.
const linkify = new LinkifyIt({ fuzzyLink: true, fuzzyEmail: false }).tlds(tlds)
// Bare-domain matches (no scheme) default to http:// internally — force https,
// since virtually every real host supports it today. Explicit http://... links
// a developer wrote on purpose are left alone.
linkify.normalize = function (match) {
  if (!match.schema) match.url = `https://${match.url}`
}

// Comment bodies are sanitised on write, which escapes `&`, `<`, `>` and `"`
// in the text — "R&D budget" is stored as "R&amp;D budget". Every consumer
// below wants plain text, not HTML: React renders these as text nodes, the
// edit box puts them in a textarea, and the copy button writes them to the
// clipboard. So the entities have to be decoded back or they read literally.
//
// Re-saving is safe either way — sanitize-html parses `&amp;` back to `&`
// before re-escaping, so entities never compound across edits.
const commentText = (html: string | null | undefined) =>
  decodeEntities(contentPreview(html ?? '', Infinity))

const fmtRupees = (paise: number) => `₹${(paise / 100).toFixed(2)}`

const bumpPostCount = () =>
  store.dispatch(
    agentsApi.util.updateQueryData('getForumStats', undefined, (draft) => {
      draft.postCount += 1
    })
  )

function renderContent(
  text: string,
  mentions: { name: string; userId: string }[] = [],
  currentUserId?: string,
  agentMentions: { name: string; agentId: string }[] = []
): React.ReactNode {
  // Explicit `[label](https://...)` syntax — kept for developers who want a
  // custom display label instead of the raw URL. Bare URLs/domains (with or
  // without a scheme) are handled separately below via linkify-it.
  const LINK_RE = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/

  // Runs linkify-it over a plain-text segment and interleaves the results with
  // @mention parsing. linkify-it already trims trailing sentence punctuation
  // and normalizes bare domains to a full https:// href.
  function linkifyText(input: string, keyBase: number): React.ReactNode[] {
    const matches = linkify.match(input)
    if (!matches || matches.length === 0) return parseMentions(input, keyBase)
    const nodes: React.ReactNode[] = []
    let cursor = 0
    let key = keyBase
    for (const m of matches) {
      if (m.index > cursor) {
        nodes.push(...parseMentions(input.slice(cursor, m.index), key))
        key += 100
      }
      nodes.push(<a key={key++} href={m.url} target="_blank" rel="noopener noreferrer" className={s.mdLink}>{m.text}</a>)
      cursor = m.lastIndex
    }
    if (cursor < input.length) nodes.push(...parseMentions(input.slice(cursor), key))
    return nodes
  }

  function parseMarkdown(input: string, keyBase: number): React.ReactNode[] {
    const parts = input.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g)
    const nodes: React.ReactNode[] = []
    let key = keyBase
    for (const part of parts) {
      const linkMatch = LINK_RE.exec(part)
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        // Recurse instead of pushing raw text — agents commonly wrap a domain in
        // **bold** for emphasis (as seen live: "**incometax.gov.in**"), and without
        // this the outer bold match would consume it before the link checks below
        // ever get a look at what's inside.
        nodes.push(<strong key={key++} className={s.mdBold}>{parseMarkdown(part.slice(2, -2), key)}</strong>)
        key += 1000
      } else if (linkMatch) {
        nodes.push(<a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className={s.mdLink}>{linkMatch[1]}</a>)
      } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2 && !part.startsWith('**')) {
        nodes.push(<em key={key++} className={s.mdItalic}>{parseMarkdown(part.slice(1, -1), key)}</em>)
        key += 1000
      } else {
        nodes.push(...linkifyText(part, key))
        key += 100
      }
    }
    return nodes
  }

  function parseMentions(input: string, keyOffset: number): React.ReactNode[] {
    const allMentions = [
      ...mentions.map(m => ({ name: m.name, userId: m.userId, isAgent: false })),
      ...agentMentions.map(m => ({ name: m.name, userId: m.agentId, isAgent: true })),
    ]
    if (!allMentions.length) return [input]
    const parts: React.ReactNode[] = []
    let remaining = input
    let key = keyOffset
    while (remaining.length > 0) {
      const atIdx = remaining.indexOf('@')
      if (atIdx === -1) { parts.push(remaining); break }
      if (atIdx > 0) parts.push(remaining.slice(0, atIdx))
      const after = remaining.slice(atIdx + 1)
      let matched: { name: string; userId: string; isAgent: boolean } | null = null
      let matchedLen = 0
      for (const m of allMentions) {
        const lower = after.toLowerCase()
        if (lower.startsWith(m.name.toLowerCase())) {
          const end = m.name.length
          const next = after[end]
          if (!next || /[\s,!?.]/.test(next)) {
            if (m.name.length > matchedLen) { matched = m; matchedLen = m.name.length }
          }
        }
      }
      if (matched) {
        if (matched.isAgent) {
          parts.push(
            <span key={key++} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontWeight: 700, color: 'var(--accent-primary)', background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '4px', padding: '0 0.3rem', fontSize: '0.9em', cursor: 'default' }}>
              🤖{matched.name}
            </span>
          )
        } else {
          const isOwn = matched.userId === currentUserId
          parts.push(
            <UserHoverCard key={key++} userId={matched.userId} currentUserId={currentUserId} displayName={matched.name} avatarUrl={null}>
              <span style={{ fontWeight: 700, color: isOwn ? 'var(--text-primary)' : 'var(--accent-primary)', cursor: isOwn ? 'default' : 'pointer' }}>
                {matched.name}
              </span>
            </UserHoverCard>
          )
        }
        remaining = after.slice(matchedLen)
      } else {
        parts.push('@')
        remaining = after
      }
      key++
    }
    return parts
  }

  if (!mentions.length && !agentMentions.length && !text.includes('**') && !text.includes('*') && !linkify.test(text)) return text
  return <>{parseMarkdown(text, 0)}</>
}

function AIThinkingIndicator({ agentName, agentIcon }: { agentName: string; agentIcon?: string | null }) {
  return (
    <div className={s.aiThinkingInner}>
      <div className={s.aiIconBox}>{agentIcon ?? '🤖'}</div>
      <span className={s.aiThinkingName}>{agentName}</span>
      <span className={s.aiTagPill}>AI</span>
      <div className={s.aiThinkingDotsRow}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--accent-primary)', opacity: 0.7, animation: `aiDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <style>{`@keyframes aiDot { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }`}</style>
    </div>
  )
}

export default function CommentCard({ comment, depth = 0, isAuthenticated, accessToken, discussionId, currentUserId, likedCommentIds, onPromptLogin, onCountIncrement, aiThinking = [], streamingComments = new Map() }: {
  comment: Comment; depth?: number; isAuthenticated: boolean; accessToken: string | null
  discussionId: string; currentUserId?: string; likedCommentIds: Set<string>
  onPromptLogin: () => void; onCountIncrement: () => void
  aiThinking?: { agentId: string; agentName: string; agentIcon: string | null; parentCommentId: string | null }[]
  streamingComments?: Map<string, string>
}) {
  const [showReply, setShowReply] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [showCommentReport, setShowCommentReport] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(commentText(comment.content))
  const [editSaving, setEditSaving] = useState(false)
  const [localContent, setLocalContent] = useState(comment.content)
  const [localEditedAt, setLocalEditedAt] = useState(comment.editedAt)
  const [unlocked, setUnlocked] = useState(comment.unlocked ?? false)
  const toast = useToast()
  const [unlocking, setUnlocking] = useState(false)
  const [unlockError, setUnlockError] = useState<string | null>(null)
  const AUTO_EXPAND_THRESHOLD = 3
  const shouldAutoExpand = (comment._count?.replies ?? comment.replies?.length ?? 0) <= AUTO_EXPAND_THRESHOLD
  const [repliesOpen, setRepliesOpen] = useState(shouldAutoExpand)
  const [loadingReplies, setLoadingReplies] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [replies, setReplies] = useState<Comment[]>(comment.replies ?? [])
  const [liked, setLiked] = useState(() => likedCommentIds.has(comment.id))
  const [likeCount, setLikeCount] = useState(comment.voteScore)
  const [localReplyCount, setLocalReplyCount] = useState(comment._count?.replies ?? 0)
  const totalReplies = localReplyCount
  const preloaded = comment.replies?.length ?? 0
  const [repliesLoaded, setRepliesLoaded] = useState(preloaded > 0 || shouldAutoExpand)
  const [replyHasMore, setReplyHasMore] = useState(totalReplies > preloaded)
  const [replyCursor, setReplyCursor] = useState<string | null>(
    comment.replies?.length ? comment.replies[comment.replies.length - 1]?.id ?? null : null
  )
  const [hovered, setHovered] = useState(false)
  const [feedbackGiven, setFeedbackGiven] = useState<'helpful' | 'not_helpful' | null>(null)
  const [feedbackInFlight, setFeedbackInFlight] = useState(false)
  const [copied, setCopied] = useState(false)

  const isAI = comment.type === 'ai_response'
  const name = isAI ? (comment.agent?.name ?? 'AI Agent') : (comment.author?.profile?.displayName ?? 'User')
  const avatarUrl = !isAI ? comment.author?.profile?.avatarUrl : null
  const agentIcon = isAI ? (comment.agent?.icon ?? '🤖') : null
  const isLocked = !!comment.isPremium && !unlocked
  const rawContent = commentText(localContent)
  const isTruncated = rawContent.length > MAX_CONTENT_LEN
  const displayContent = isTruncated && !expanded ? rawContent.slice(0, MAX_CONTENT_LEN) + '…' : rawContent
  const isOwn = comment.author?.id === currentUserId && !isAI

  const handleUnlock = async () => {
    if (!isAuthenticated) { onPromptLogin(); return }
    if (unlocking || unlocked) return
    setUnlocking(true); setUnlockError(null)
    try {
      // Step 1: Create a Razorpay order for this comment
      const orderData = await api.post<{ orderId: string; amount: number; currency: string; keyId: string }>(
        'comments', ENDPOINTS.comments.createOrder(comment.id), {}, accessToken
      )

      // Step 2 & 3: Load checkout.js (once) and open the payment modal
      const payment = await openRazorpayCheckout({
        keyId:       orderData.keyId,
        orderId:     orderData.orderId,
        amount:      orderData.amount,
        currency:    orderData.currency,
        description: 'Unlock premium response',
      })

      // Step 4: Verify payment on the backend and get the real content
      const d = await api.post<{ comment: Comment; alreadyUnlocked: boolean }>(
        'comments', ENDPOINTS.comments.unlock(comment.id),
        {
          razorpayPaymentId: payment.razorpayPaymentId,
          razorpayOrderId:   payment.razorpayOrderId,
          razorpaySignature: payment.razorpaySignature,
        },
        accessToken
      )
      setLocalContent(d.comment.content)
      setUnlocked(true)
      toast.success(`Payment of ₹${(orderData.amount / 100).toLocaleString('en-IN')} received — ${comment.agent?.name ?? 'premium'} response unlocked. Your invoice is in Settings → Billing.`)
    } catch (err: any) {
      if (err?.message === 'cancelled') {
        // user dismissed — no-op, button re-enables via finally
      } else if (err instanceof ApiError && err.status === 409) {
        // order endpoint says already unlocked — state is out of sync with the server.
        // Real content isn't in localContent (it was sent as a placeholder), so a
        // refresh is the only way to show it.
        setUnlockError('Already unlocked — please refresh the page to see this response.')
      } else {
        setUnlockError(err?.message ?? 'Unlock failed')
      }
    } finally {
      setUnlocking(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!editContent.trim() || editSaving) return
    setEditSaving(true)
    try {
      const d = await api.put<{ comment: Comment }>('comments', ENDPOINTS.comments.update(comment.id), { content: editContent.trim() }, accessToken)
      setLocalContent(d.comment.content); setLocalEditedAt(d.comment.editedAt)
      setEditContent(commentText(d.comment.content)); setIsEditing(false)
    } catch {}
    setEditSaving(false)
  }

  const handleCopyResponse = async () => {
    try {
      await navigator.clipboard.writeText(commentText(localContent))
      setCopied(true); setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
    } catch {}
  }

  const handleFeedback = async (rating: 'helpful' | 'not_helpful') => {
    if (!isAuthenticated) { onPromptLogin(); return }
    if (feedbackInFlight) return
    setFeedbackInFlight(true)
    const prev = feedbackGiven
    setFeedbackGiven(prev === rating ? null : rating)
    try {
      const d = await api.post<{ success: boolean; rating: 'helpful' | 'not_helpful' | null }>('comments', ENDPOINTS.comments.feedback(comment.id), { rating }, accessToken)
      setFeedbackGiven(d.rating)
    } catch { setFeedbackGiven(prev) } finally { setFeedbackInFlight(false) }
  }

  const cardRef = useRef<HTMLDivElement>(null)
  const pushReply = useCallback((reply: Comment) => {
    if (reply.parentId !== comment.id) return
    setReplies((prev) => prev.some((r) => r.id === reply.id) ? prev : [reply, ...prev])
    setLocalReplyCount((c) => c + 1); setRepliesLoaded(true); setRepliesOpen(true)
  }, [comment.id])

  const repliesFetchedRef = useRef(false)
  const lineRef = useRef<HTMLButtonElement>(null)
  const repliesSectionRef = useRef<HTMLDivElement>(null)

  // Trim the collapse line so it stops at the last reply's avatar, not the card bottom
  useEffect(() => {
    if (!repliesOpen || !lineRef.current || !repliesSectionRef.current) return
    const trim = () => {
      const line = lineRef.current
      const section = repliesSectionRef.current
      if (!line || !section) return
      const lastChild = section.lastElementChild as HTMLElement | null
      if (!lastChild) return
      line.style.marginBottom = '0px'
      const lineRect = line.getBoundingClientRect()
      const lastRect = lastChild.getBoundingClientRect()
      const overshoot = lineRect.bottom - (lastRect.top + 13)
      if (overshoot > 0) line.style.marginBottom = `${overshoot}px`
    }
    trim()
    const ro = new ResizeObserver(trim)
    ro.observe(repliesSectionRef.current)
    return () => ro.disconnect()
  }, [repliesOpen, replies.length])

  const handleOpenReplies = async (force = false) => {
    setRepliesOpen(true)
    if (repliesFetchedRef.current && !force) return
    repliesFetchedRef.current = true
    setLoadingReplies(true)
    try {
      const d = await api.get<{ data: Comment[]; hasMore: boolean; nextCursor: string | null }>('comments', `${ENDPOINTS.comments.replies(comment.id)}?limit=500`, accessToken)
      setReplies(d.data); setReplyHasMore(false); setReplyCursor(null); setRepliesLoaded(true)
    } catch {}
    setLoadingReplies(false)
  }

  useEffect(() => {
    if (totalReplies > 0) void handleOpenReplies()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isThinkingForThis = aiThinking.some(t => t.parentCommentId === comment.id)
    if (isThinkingForThis) { setRepliesOpen(true); setRepliesLoaded(true) }
  }, [aiThinking, comment.id])

  useEffect(() => {
    const el = cardRef.current; if (!el) return
    const h1 = (e: Event) => { pushReply((e as CustomEvent<Comment>).detail) }
    const h2 = (e: Event) => { const { likeCount: c } = (e as CustomEvent<{ likeCount: number }>).detail; setLikeCount(c) }
    const h3 = (e: Event) => { const d = (e as CustomEvent<{ commentId: string; content: string; editedAt: string }>).detail; if (d.commentId === comment.id) { setLocalContent(d.content); setLocalEditedAt(d.editedAt) } }
    const h5 = () => { void handleOpenReplies(true) }
    el.addEventListener(DOM_EVENTS.sseNewReply, h1); el.addEventListener(DOM_EVENTS.sseLikeUpdate, h2); el.addEventListener(DOM_EVENTS.sseCommentEdited, h3); el.addEventListener(DOM_EVENTS.deeplinkOpenReplies, h5)
    return () => { el.removeEventListener(DOM_EVENTS.sseNewReply, h1); el.removeEventListener(DOM_EVENTS.sseLikeUpdate, h2); el.removeEventListener(DOM_EVENTS.sseCommentEdited, h3); el.removeEventListener(DOM_EVENTS.deeplinkOpenReplies, h5) }
  }, [pushReply, comment.id])

  const handleSubmitReply = async (content: string) => {
    const d = await api.post<{ comment: Comment }>('comments', ENDPOINTS.comments.create(discussionId), { content, parentId: comment.id }, accessToken)
    setReplies((prev) => [d.comment, ...prev]); setLocalReplyCount((c) => c + 1)
    setRepliesLoaded(true); setRepliesOpen(true); bumpPostCount(); onCountIncrement(); setShowReply(false)
  }

  const handleReplyClick = () => {
    if (!isAuthenticated) { onPromptLogin(); return }
    if (totalReplies > 0 && !repliesOpen) void handleOpenReplies()
    setShowReply(!showReply)
  }

  const prefetchReplies = async () => {
    if (repliesLoaded || loadingReplies || totalReplies === 0) return
    setLoadingReplies(true)
    try {
      const d = await api.get<{ data: Comment[]; hasMore: boolean; nextCursor: string | null }>('comments', `${ENDPOINTS.comments.replies(comment.id)}?limit=500`, accessToken)
      setReplies(d.data); setReplyHasMore(false); setReplyCursor(null); setRepliesLoaded(true)
    } catch {}
    setLoadingReplies(false)
  }

  const handleLoadMore = async () => {
    if (loadingMore || !replyHasMore) return
    setLoadingMore(true)
    try {
      const params = new URLSearchParams({ limit: String(REPLIES_PAGE_SIZE) })
      if (replyCursor) params.set('cursor', replyCursor)
      const d = await api.get<{ data: Comment[]; hasMore: boolean; nextCursor: string | null }>('comments', `${ENDPOINTS.comments.replies(comment.id)}?${params}`, accessToken)
      setReplies((prev) => [...prev, ...d.data]); setReplyHasMore(d.hasMore); setReplyCursor(d.nextCursor)
    } catch {}
    setLoadingMore(false)
  }

  const isDeepLinkTarget = typeof window !== 'undefined' && window.location.hash === `#comment-${comment.id}`

  return (
    <div ref={cardRef} data-comment-id={comment.id} className={s.commentOuter} style={{ '--comment-depth': String(depth) } as React.CSSProperties}>
      {isDeepLinkTarget && (
        <style>{`@keyframes commentHighlight { 0%{background:rgba(124,58,237,0.22);border-color:rgba(124,58,237,0.7)} 70%{background:rgba(124,58,237,0.22);border-color:rgba(124,58,237,0.7)} 100%{background:transparent;border-color:transparent} }`}</style>
      )}
      <div className={s.commentRow}>
        <div className={s.commentMain}>
          <div className={s.commentWithCollapseWrap}>
            {totalReplies > 0 && (
              <div className={s.collapseCol}>
                <button
                  className={s.collapseTopBtn}
                  onClick={() => repliesOpen ? setRepliesOpen(false) : void handleOpenReplies()}
                  onMouseEnter={() => { if (!repliesOpen) void prefetchReplies() }}
                  disabled={loadingReplies}
                  title={repliesOpen ? 'Collapse thread' : 'Expand thread'}
                  aria-label={repliesOpen ? 'Collapse thread' : 'Expand thread'}
                  style={{ cursor: loadingReplies ? 'not-allowed' : 'pointer' }}
                >
                  <span className={s.collapseIcon}>{repliesOpen ? '−' : (loadingReplies ? '…' : '+')}</span>
                </button>
                {repliesOpen && (
                  <button
                    ref={lineRef}
                    className={s.collapseLineFill}
                    onClick={() => setRepliesOpen(false)}
                    title="Collapse thread"
                    aria-label="Collapse thread"
                  />
                )}
              </div>
            )}
            <div className={s.commentBodyAndReplies}>
              <div className={s.commentCardBg}
                style={{ background: isAI ? COLOR_AI_BG : (hovered && !isEditing ? 'var(--bg-elevated)' : 'transparent'), padding: depth === 0 ? '0.65rem 0.75rem' : '0.5rem 0.6rem', marginBottom: '0.15rem', border: isAI ? (isLocked ? '1px solid rgba(234,179,8,0.45)' : `1px solid ${COLOR_AI_BORDER}`) : `1px solid ${isDeepLinkTarget ? 'rgba(124,58,237,0.7)' : 'transparent'}`, borderLeft: isLocked ? '3px solid rgba(234,179,8,0.7)' : isDeepLinkTarget ? '3px solid var(--accent-primary)' : undefined, borderRadius: '8px', transition: 'background 0.1s', animation: isDeepLinkTarget ? 'commentHighlight 4s ease-out 1 forwards' : undefined }}
                onMouseEnter={() => { if (!isAI) setHovered(true) }}
                onMouseLeave={() => { if (!isAI) setHovered(false) }}
              >
                <div className={s.commentAuthorRow}>
                  {isAI ? (
                    <div className={s.aiIconBox}>{agentIcon}</div>
                  ) : comment.author?.id ? (
                    <UserHoverCard userId={comment.author.id} currentUserId={currentUserId} displayName={name} avatarUrl={avatarUrl}>
                      <Avatar name={name} avatarUrl={avatarUrl} size={22} />
                    </UserHoverCard>
                  ) : (
                    <Avatar name={name} avatarUrl={avatarUrl} size={22} />
                  )}
                  {!isAI && comment.author?.id ? (
                    <UserHoverCard userId={comment.author.id} currentUserId={currentUserId} displayName={name} avatarUrl={avatarUrl}>
                      <span className={s.authorName} onMouseEnter={(e) => ((e.currentTarget as HTMLSpanElement).style.color = 'var(--accent-primary)')} onMouseLeave={(e) => ((e.currentTarget as HTMLSpanElement).style.color = 'var(--text-primary)')}>{name}</span>
                    </UserHoverCard>
                  ) : (
                    <span style={{ fontWeight: 600, fontSize: '0.8rem', color: isAI ? 'var(--accent-primary)' : 'var(--text-primary)' }}>{name}</span>
                  )}
                  {!isAI && comment.author?.profile?.flair && (
                    <span style={{ fontSize: '0.65rem', background: 'rgba(124,58,237,0.08)', color: 'var(--accent-primary)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '20px', padding: '0.05rem 0.4rem', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>{comment.author.profile.flair}</span>
                  )}
                  {isAI && <span className={s.aiTagPill}>AI</span>}
                  {comment.isPremium && (
                    <span style={{ fontSize: '0.62rem', background: 'rgba(234,179,8,0.12)', color: '#b45309', border: '1px solid rgba(234,179,8,0.35)', borderRadius: '20px', padding: '0.05rem 0.45rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.01em' }}>⭐ Premium</span>
                  )}
                  <span className={s.commentTime}>{timeAgoShort(comment.createdAt)}</span>
                </div>

                {isEditing ? (
                  <div className={s.commentEditWrap}>
                    <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} autoFocus rows={3} className={s.editTextarea}
                      onKeyDown={(e) => { if (e.key === 'Escape') setIsEditing(false); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void handleSaveEdit() }}
                    />
                    <div className={s.commentEditActions}>
                      <button onClick={() => setIsEditing(false)} className={s.editCancelBtn}>Cancel</button>
                      <button onClick={() => void handleSaveEdit()} disabled={!editContent.trim() || editSaving}
                        style={{ padding: '0.25rem 0.7rem', background: editContent.trim() && !editSaving ? 'var(--accent-primary)' : 'var(--bg-elevated)', color: editContent.trim() && !editSaving ? 'white' : 'var(--text-muted)', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: editContent.trim() && !editSaving ? 'pointer' : 'not-allowed', fontSize: '0.75rem' }}>
                        {editSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : isLocked ? (
                  // `localContent` here is a fake-blur placeholder from the backend, never the
                  // real text — see CommentsService.maskPremiumComments. Safe to render as-is.
                  <div style={{ position: 'relative', marginTop: '0.25rem', borderRadius: '8px', overflow: 'hidden', minHeight: '96px', maxHeight: '96px' }}>
                    <div className={s.commentBody} style={{ filter: 'blur(6px)', opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }} aria-hidden="true">
                      {localContent}
                    </div>
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '0.75rem',
                      background: 'rgba(20,14,4,0.45)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
                    }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'white', textAlign: 'center', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                        🔒 Premium response
                      </span>
                      <button
                        onClick={() => void handleUnlock()}
                        disabled={unlocking}
                        style={{ padding: '0.4rem 0.9rem', background: 'rgba(234,179,8,0.9)', color: '#1c1100', border: 'none', borderRadius: '6px', fontWeight: 700, fontSize: '0.78rem', cursor: unlocking ? 'not-allowed' : 'pointer', opacity: unlocking ? 0.7 : 1, flexShrink: 0, boxShadow: '0 2px 10px rgba(234,179,8,0.35)' }}
                      >
                        {unlocking ? 'Unlocking…' : `Unlock for ${fmtRupees(comment.premiumAmountPaise ?? 0)}`}
                      </button>
                      {unlockError && <span style={{ fontSize: '0.72rem', color: '#fca5a5', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{unlockError}</span>}
                    </div>
                  </div>
                ) : (
                  <div className={isAI ? undefined : s.commentBody}>
                    {isAI
                      ? <AIMarkdownContent content={displayContent} />
                      : renderContent(displayContent, comment.mentions, currentUserId, comment.agentMentions)
                    }
                    {isTruncated && <button onClick={() => setExpanded(!expanded)} className={s.expandBtn}>{expanded ? 'Show less' : 'Read more'}</button>}
                    {localEditedAt && <span className={s.commentEdited}>(edited)</span>}
                  </div>
                )}

                {!isEditing && !isLocked && (
                  <div className={s.commentActions}>
                    {!isAI && (
                      <LikeButton commentId={comment.id} liked={liked} likeCount={likeCount}
                        isAuthenticated={isAuthenticated} accessToken={accessToken}
                        onToggle={(l, c) => { setLiked(l); setLikeCount(c) }}
                        onPromptLogin={onPromptLogin}
                      />
                    )}
                    <Tooltip text="Reply">
                      <button onClick={handleReplyClick} className={s.commentActionBtn}>↩ Reply</button>
                    </Tooltip>
                    {isOwn && (
                      <Tooltip text="Edit comment">
                        <button onClick={() => setIsEditing(true)} className={s.iconBtn}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-primary)' }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}>✏️</button>
                      </Tooltip>
                    )}
                    {REPORTING_ENABLED && !isAI && comment.author?.id !== currentUserId && (
                      <Tooltip text="Report comment">
                        <button onClick={() => { if (!isAuthenticated) { onPromptLogin(); return } setShowCommentReport(true) }} className={s.iconBtn}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = COLOR_ERROR }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}>🚩</button>
                      </Tooltip>
                    )}
                    {isAI && (
                      <>
                        <div className={s.actionDivider} />
                        <Tooltip text={copied ? 'Copied!' : 'Copy response'}>
                          <button onClick={() => void handleCopyResponse()}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: copied ? 'rgba(34,197,94,0.1)' : 'transparent', color: copied ? COLOR_SUCCESS : 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.15s', flexShrink: 0 }}
                            onMouseEnter={(e) => { if (!copied) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)' }}
                            onMouseLeave={(e) => { if (!copied) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}>
                            {copied ? '✓' : '⎘'}
                          </button>
                        </Tooltip>
                        <Tooltip text={feedbackGiven === 'helpful' ? 'Remove helpful rating' : 'Helpful'}>
                          <button onClick={() => void handleFeedback('helpful')} disabled={feedbackInFlight}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: feedbackGiven === 'helpful' ? 'rgba(34,197,94,0.12)' : 'transparent', color: feedbackGiven === 'helpful' ? COLOR_SUCCESS : 'var(--text-muted)', cursor: feedbackInFlight ? 'not-allowed' : 'pointer', fontSize: '0.8rem', transition: 'all 0.15s', flexShrink: 0, opacity: feedbackInFlight ? 0.6 : 1 }}
                            onMouseEnter={(e) => { if (!feedbackInFlight) (e.currentTarget as HTMLButtonElement).style.color = COLOR_SUCCESS }}
                            onMouseLeave={(e) => { if (feedbackGiven !== 'helpful') (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}>👍</button>
                        </Tooltip>
                        <Tooltip text={feedbackGiven === 'not_helpful' ? 'Remove not helpful rating' : 'Not helpful'}>
                          <button onClick={() => void handleFeedback('not_helpful')} disabled={feedbackInFlight}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: feedbackGiven === 'not_helpful' ? 'rgba(239,68,68,0.12)' : 'transparent', color: feedbackGiven === 'not_helpful' ? COLOR_ERROR : 'var(--text-muted)', cursor: feedbackInFlight ? 'not-allowed' : 'pointer', fontSize: '0.8rem', transition: 'all 0.15s', flexShrink: 0, opacity: feedbackInFlight ? 0.6 : 1 }}
                            onMouseEnter={(e) => { if (!feedbackInFlight) (e.currentTarget as HTMLButtonElement).style.color = COLOR_ERROR }}
                            onMouseLeave={(e) => { if (feedbackGiven !== 'not_helpful') (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}>👎</button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                )}

                {showReply && (
                  <div className={s.repliesWrap}>
                    <ReplyComposer onSubmit={handleSubmitReply} onCancel={() => setShowReply(false)} placeholder={`Reply to ${name}...`} discussionId={discussionId} />
                  </div>
                )}
                {showCommentReport && <ReportModal targetType="comment" targetId={comment.id} onClose={() => setShowCommentReport(false)} />}
              </div>

              {totalReplies > 0 && !repliesOpen && (
                <button onClick={() => void handleOpenReplies()} onMouseEnter={() => void prefetchReplies()} disabled={loadingReplies} className={s.showRepliesBtn} style={{ cursor: loadingReplies ? 'not-allowed' : 'pointer' }}>
                  {loadingReplies ? 'Loading...' : `View ${totalReplies} ${totalReplies === 1 ? 'reply' : 'replies'}`}
                </button>
              )}

              {totalReplies > 0 && repliesOpen && (
                <div className={s.repliesSection} ref={repliesSectionRef}>
                  {aiThinking.filter(t => t.parentCommentId === comment.id).map(t => {
                    const streamingText = streamingComments.get(t.agentId)
                    const isStreaming = streamingText !== undefined && streamingText.length > 0
                    return (
                      <div key={t.agentId} className={s.replyWithConnector}>
                        {isStreaming ? (
                          <div className={s.commentCardBg} style={{ background: COLOR_AI_BG, padding: '0.5rem 0.6rem', border: `1px solid ${COLOR_AI_BORDER}`, borderRadius: '8px', marginBottom: '0.15rem' }}>
                            <div className={s.commentAuthorRow}>
                              <div className={s.aiIconBox}>{t.agentIcon ?? '🤖'}</div>
                              <span style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--accent-primary)' }}>{t.agentName}</span>
                              <span className={s.aiTagPill}>AI</span>
                            </div>
                            <div className={s.commentBody}>
                              {streamingText}<span style={{ display: 'inline-block', width: '2px', height: '1em', background: 'var(--accent-primary)', marginLeft: '1px', verticalAlign: 'text-bottom', animation: 'blink 0.8s step-end infinite' }} />
                              <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
                            </div>
                          </div>
                        ) : (
                          <AIThinkingIndicator agentName={t.agentName} agentIcon={t.agentIcon} />
                        )}
                      </div>
                    )
                  })}
                  {replies.map((r) => (
                    <div key={r.id} className={s.replyWithConnector}>
                      <CommentCard comment={r} depth={Math.min(depth + 1, 1)}
                        isAuthenticated={isAuthenticated} accessToken={accessToken}
                        discussionId={discussionId} currentUserId={currentUserId}
                        likedCommentIds={likedCommentIds}
                        onPromptLogin={onPromptLogin} onCountIncrement={onCountIncrement}
                        aiThinking={aiThinking} streamingComments={streamingComments}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
